/**
 * `pending-rbac-vs-audience-tier-composition` v0 — central registry
 * of UI room keys + their RBAC + audience-tier requirements.
 *
 * Locked decisions (2026-05-31, this ship):
 *   1. **Most-restrictive wins.** A row is visible only when BOTH the
 *      RBAC role AND the audience tier meet the room's minimum.
 *   2. **v1 operator control = `audienceRoleMap` (role-derived).**
 *      Schema deferred — v0 ships the resolver only.
 *   3. **Failure mode is split:** RBAC denials hide the row entirely
 *      (don't leak that the protected resource exists); audience
 *      denials show the row disabled with a "switch to Developer
 *      view" tooltip (preserves the existing affordance).
 *   4. **v0 sweeps all 5 Round-2 surfaces** (DB-query fields, raw
 *      cron, Script step editor, Environment Variables, Usage
 *      CI/CD + AI Keys).
 *
 * v0 is FE-only — the registry lives in `@rynora/contracts` because
 * v1 (server-side `audienceRoleMap` enforcement) will need the same
 * room keys without re-defining them. RBAC enforcement on the
 * server stays as it is today (existing `requireRole` middleware);
 * this resolver only fixes the SPA's *gating appearance*.
 */

import { z } from 'zod';

/**
 * Stable kebab-case room keys. Adding a new room: register it here,
 * pick the right (minRole, minAudience), then call
 * `useEffectiveAccess(roomKey)` from the consumer.
 */
export const EffectiveAccessRoomKeySchema = z.enum([
  // Round 2 Ship 4 — DB-query step fields (host / port / connection-string / SQL).
  'db-query.fields',
  // Round 2 Ship 5 — ScheduleManager raw cron textarea.
  'schedule.raw-cron',
  // Round 2 Ship 6 — ScriptStepEditor (TypeScript / RunJS sandbox).
  'script-step.editor',
  // Round 2 Ship 7 — EnvironmentManager Variables/Secrets tab.
  'environment.variables',
  // Round 2 Ship 9 — UsageDashboard CI/CD keys tab.
  'usage.cicd-keys',
  // Round 2 Ship 9 — UsageDashboard AI Keys (BYOK) tab.
  'usage.ai-keys',
]);
export type EffectiveAccessRoomKey = z.infer<typeof EffectiveAccessRoomKeySchema>;

/**
 * RBAC roles supported by this resolver. Mirrors the existing
 * `OrgRoleSchema` in `orgs.ts` but kept narrow (no SCIM /
 * platform-admin variants — those are handled separately).
 */
export const EffectiveAccessRoleSchema = z.enum(['viewer', 'member', 'admin', 'owner']);
export type EffectiveAccessRole = z.infer<typeof EffectiveAccessRoleSchema>;

export const EffectiveAccessAudienceSchema = z.enum(['business', 'developer', 'expert']);
export type EffectiveAccessAudience = z.infer<typeof EffectiveAccessAudienceSchema>;

/**
 * Why a room is denied. Drives the failure-mode UX (locked
 * decision #3): RBAC denials hide; audience denials show-disabled.
 *   - `'rbac'`     — RBAC alone failed; audience would have allowed.
 *   - `'audience'` — audience alone failed; RBAC would have allowed.
 *   - `'both'`     — both failed; treated as RBAC for hide-or-show
 *                    purposes (don't leak the resource exists).
 */
export const EffectiveAccessReasonSchema = z.enum(['rbac', 'audience', 'both']);
export type EffectiveAccessReason = z.infer<typeof EffectiveAccessReasonSchema>;

export interface EffectiveAccessResult {
  allowed: boolean;
  /** `null` when allowed, populated when denied. */
  reason: EffectiveAccessReason | null;
  /** The minimum audience tier this room requires. */
  audienceRequired: EffectiveAccessAudience;
  /** The minimum RBAC role this room requires. */
  rbacRequired: EffectiveAccessRole;
}

const ROLE_RANK: Record<EffectiveAccessRole, number> = {
  viewer: 0,
  member: 1,
  admin: 2,
  owner: 3,
};

const AUDIENCE_RANK: Record<EffectiveAccessAudience, number> = {
  business: 0,
  developer: 1,
  expert: 2,
};

/**
 * Static room registry. Tuning a row's gate updates the rule for
 * every consumer in lock-step.
 *
 * The audience minimums match what the existing FE renders today
 * (so the v0 sweep is a behaviour-preserving move from "audience
 * gate alone" to "audience AND RBAC gate"). The RBAC minimums
 * codify what the SERVER already enforces — so a `viewer` user who
 * happened to flip themselves to Developer no longer sees rows
 * the server would 403 anyway.
 */
export const EFFECTIVE_ACCESS_ROOMS: Record<
  EffectiveAccessRoomKey,
  { minRole: EffectiveAccessRole; minAudience: EffectiveAccessAudience }
> = {
  'db-query.fields': { minRole: 'member', minAudience: 'developer' },
  'schedule.raw-cron': { minRole: 'member', minAudience: 'developer' },
  'script-step.editor': { minRole: 'admin', minAudience: 'expert' },
  'environment.variables': { minRole: 'admin', minAudience: 'developer' },
  'usage.cicd-keys': { minRole: 'admin', minAudience: 'developer' },
  'usage.ai-keys': { minRole: 'admin', minAudience: 'developer' },
};

/**
 * Pure resolver — used by both the React hook and unit tests.
 * Most-restrictive wins (locked decision #1).
 */
export function resolveEffectiveAccess(
  roomKey: EffectiveAccessRoomKey,
  role: EffectiveAccessRole,
  audience: EffectiveAccessAudience,
): EffectiveAccessResult {
  const room = EFFECTIVE_ACCESS_ROOMS[roomKey];
  const rbacOk = ROLE_RANK[role] >= ROLE_RANK[room.minRole];
  const audienceOk = AUDIENCE_RANK[audience] >= AUDIENCE_RANK[room.minAudience];
  if (rbacOk && audienceOk) {
    return { allowed: true, reason: null, audienceRequired: room.minAudience, rbacRequired: room.minRole };
  }
  let reason: EffectiveAccessReason;
  if (!rbacOk && !audienceOk) reason = 'both';
  else if (!rbacOk) reason = 'rbac';
  else reason = 'audience';
  return {
    allowed: false,
    reason,
    audienceRequired: room.minAudience,
    rbacRequired: room.minRole,
  };
}
